import React from "react";
import Chart from "chart.js/auto";
import { Bar } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(annotationPlugin);

// Configuración de las variables de Antifortalezas:
// campo: nombre del campo en el item individual
// avgKey: nombre del campo AVG en Listado1Total
// sdKey: nombre del campo STDEV en Listado1Total
// flagKey: campo del item con el veredicto de anti-fortaleza ("D" = debilidad).
//   Es la MISMA fuente de verdad que Total_AF y la lista de hallazgos, calculada
//   contra la NORMA de referencia (no contra la media del grupo). El color rojo
//   de la barra se decide con este flag; el Z-score solo define el LARGO.
const AF_CONFIG = [
  {
    nombre: "Prudencia",
    campo: "Prudencia",
    avgKey: "AVG_Prudencia",
    sdKey: "STDEV_Prudencia",
    flagKey: "PRU",
  },
  {
    nombre: "Autoregulación",
    campo: "Autoregulacion",
    avgKey: "AVG_Autoregulacion",
    sdKey: "STDEV_Autoregulacion",
    flagKey: "AUT",
  },
  {
    nombre: "Responsabilidad",
    campo: "Responsabilidad",
    avgKey: "AVG_Responsabilidad",
    sdKey: "STDEV_Responsabilidad",
    flagKey: "RES",
  },
  {
    nombre: "Vitalidad",
    campo: "Vitalidad",
    avgKey: "AVG_Vitalidad",
    sdKey: "STDEV_Vitalidad",
    flagKey: "VIT",
  },
  {
    nombre: "Apertura",
    campo: "Apertura",
    avgKey: "AVG_Apertura",
    sdKey: "STDEV_Apertura",
    flagKey: "APE",
  },
];

/**
 * Transforma los datos del item individual a Z-Scores.
 *
 * La barra se calcula contra la NORMA de referencia (misma base que el flag de
 * anti-fortaleza), de modo que una variable marcada como debilidad (rojo)
 * siempre cruza la línea de -0.7 SD. El Z-Score vs el grupo se conserva solo
 * como referencia para el tooltip. Si no llega la norma, se cae al grupo.
 */
const transformarDatosANormalizados = (item, totales, norma) => {
  return AF_CONFIG.map((variable) => {
    const valor = parseFloat(item[variable.campo] || 0);

    // Estadísticas del grupo (lote actual)
    const mediaGrupo = parseFloat(totales[variable.avgKey] || 0);
    const sdGrupo = parseFloat(totales[variable.sdKey] || 1);
    const zGrupo = sdGrupo !== 0 ? (valor - mediaGrupo) / sdGrupo : 0;

    // Estadísticas de la norma de referencia (base oficial del flag).
    const tieneNorma =
      norma &&
      norma[variable.avgKey] != null &&
      norma[variable.sdKey] != null;
    const mediaNorma = tieneNorma ? parseFloat(norma[variable.avgKey]) : mediaGrupo;
    const sdNorma = tieneNorma ? parseFloat(norma[variable.sdKey] || 1) : sdGrupo;
    const zNorma = sdNorma !== 0 ? (valor - mediaNorma) / sdNorma : 0;

    // La barra usa la NORMA (cae al grupo si no hay norma).
    const zBarra = tieneNorma ? zNorma : zGrupo;

    return {
      nombre: variable.nombre,
      valorNorm: parseFloat(zBarra.toFixed(2)),
      valorOriginal: valor,
      // Referencia grupo (lote)
      mediaGrupo,
      sdGrupo,
      zGrupo,
      // Referencia norma (base de la barra y del flag)
      tieneNorma,
      mediaNorma,
      sdNorma,
      zNorma,
      // Veredicto de anti-fortaleza real (vs norma). Define el color rojo.
      esAntiFortaleza: String(item[variable.flagKey]) === "D",
    };
  });
};

const GraficoZScoreAF = (props) => {
  const { item, totales, norma } = props;
  const datosNormalizados = transformarDatosANormalizados(item, totales, norma);

  const etiquetas = datosNormalizados.map((d) => d.nombre);
  const valores = datosNormalizados.map((d) => d.valorNorm);

  // Color de barra según el VEREDICTO de anti-fortaleza real (flag "D" vs norma),
  // no según el Z-Score vs grupo. Así el rojo es 100% consistente con Total_AF y
  // la lista de hallazgos: una variable marcada como debilidad aparece roja
  // aunque su Z-Score vs la media del grupo no baje de -0.7 SD. El Z-Score solo
  // define el LARGO de la barra (posición vs grupo); para las no-marcadas se
  // mantiene el degradado azul/beige como referencia visual.
  const backgroundColors = datosNormalizados.map((d) =>
    d.esAntiFortaleza
      ? "rgb(198, 107, 106)" // rojo: anti-fortaleza marcada (flag "D" vs norma)
      : d.valorNorm >= 0
        ? "rgba(18, 135, 171, 0.8)" // azul: sobre la media del grupo, sin debilidad
        : "rgb(210, 196, 187)", // beige: bajo la media del grupo, sin debilidad
  );

  const data = {
    labels: etiquetas,
    datasets: [
      {
        label: "Z-Score",
        data: valores,
        backgroundColor: backgroundColors,
        borderColor: "rgb(200, 200, 200)",
        barPercentage: 0.6,
      },
    ],
  };

  // Construir anotaciones: línea de media (0) y líneas de ±1 SD y ±0.7 SD
  const annotations = {
    mediaLine: {
      type: "line",
      xMin: 0,
      xMax: 0,
      borderColor: "rgba(220, 50, 50, 0.8)",
      borderWidth: 2,
      label: {
        content: "Media",
        enabled: true,
        position: "start",
        font: { size: 10 },
      },
    },
    sdPos: {
      type: "line",
      xMin: 1,
      xMax: 1,
      borderColor: "rgba(255, 165, 0, 0.7)",
      borderWidth: 2,
      borderDash: [5, 5],
      label: {
        content: "+1 SD",
        enabled: true,
        position: "start",
        font: { size: 10 },
      },
    },
    sdNeg: {
      type: "line",
      xMin: -1,
      xMax: -1,
      borderColor: "rgba(255, 165, 0, 0.7)",
      borderWidth: 2,
      borderDash: [5, 5],
      label: {
        content: "-1 SD",
        enabled: true,
        position: "start",
        font: { size: 10 },
      },
    },
    sd07Pos: {
      type: "line",
      xMin: 0.7,
      xMax: 0.7,
      borderColor: "rgba(192, 80, 77, 0.6)",
      borderWidth: 1.5,
      borderDash: [3, 3],
      label: {
        content: "+0.7 SD",
        enabled: true,
        position: "start",
        font: { size: 9 },
      },
    },
    sd07Neg: {
      type: "line",
      xMin: -0.7,
      xMax: -0.7,
      borderColor: "rgba(192, 80, 77, 0.6)",
      borderWidth: 1.5,
      borderDash: [3, 3],
      label: {
        content: "-0.7 SD",
        enabled: true,
        position: "start",
        font: { size: 9 },
      },
    },
  };

  const options = {
    indexAxis: "y",
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: { left: 0 },
    },
    scales: {
      x: {
        min: -2,
        max: 2,
        ticks: {
          stepSize: 1,
          font: { size: 10 },
          callback: (value) => {
            if (value === 0) return "Media";
            return value > 0 ? `+${value} SD` : `${value} SD`;
          },
        },
        title: {
          display: true,
          text: "Desviaciones Estándar (Z-Score)",
          font: { size: 11 },
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          font: { size: 12 },
        },
        afterFit: function (scaleInstance) {
          scaleInstance.width = 180;
        },
      },
    },
    plugins: {
      annotation: {
        annotations: annotations,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const d = datosNormalizados[context.dataIndex];
            // El popup muestra solo la base de la barra (norma si está
            // disponible) con etiquetas neutras: la distinción norma/grupo solo
            // se expone a superusuarios en la tabla de detalle por pregunta.
            const media = d.tieneNorma ? d.mediaNorma : d.mediaGrupo;
            const sd = d.tieneNorma ? d.sdNorma : d.sdGrupo;
            const z = d.tieneNorma ? d.zNorma : d.zGrupo;
            return [
              `Valor: ${d.valorOriginal}`,
              `Media: ${media.toFixed(2)}`,
              `SD: ${sd.toFixed(2)}`,
              `Z-Score: ${z > 0 ? "+" : ""}${z.toFixed(2)}σ`,
            ];
          },
        },
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <Bar
      options={options}
      data={data}
      style={{ height: "100%", width: "100%" }}
    />
  );
};

const GraficoZScoreAFWrapper = (props) => {
  return (
    <div style={{ height: "100%", width: "100%" }}>
      <GraficoZScoreAF {...props} />
    </div>
  );
};

export default GraficoZScoreAFWrapper;
